import React, { useState, useEffect } from 'react';
import { X, Phone, MessageSquare, FileText, Clock, Car, ChevronRight, Shield, Sparkles, Trash2, Send, Mail, ChevronDown, Calendar, AlertCircle, Eye, CheckCircle, RefreshCw, CreditCard, Activity, ChevronUp } from 'lucide-react';
import { Proposal, ProposalOffer, approveProposalOffer, declineProposalOffer, getProposal, deleteProposal, sendProposalLink, sendProposalLinkViaEmail, getProposalEvents, sendProposalLinkViaBoth } from '../lib/proposals';
import ConvertToBookingModal from './ConvertToBookingModal';
import { directQueryProposalEvents, createTestProposalEventSimple } from '../lib/debugUtils';
import { Button } from './ui/button';
import RichTextDescription from './RichTextDescription';

interface ProposalDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: Proposal;
  onUpdate?: (proposal: Proposal) => void;
  readOnly?: boolean;
  onDelete?: () => void;
}

export interface ProposalService {
  id: string;
  proposal_id: string;
  offer_id: string;
  service_id: string;
  vehicle_id: string;
  name: string;
  description: string | null;
  price: number;
  tax_exempt: boolean;
  tax_rate: number | null;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  duration_minutes: number;
  order_index: number;
  created_at: string;
  updated_at: string;
  vehicle_make?: string;
  vehicle_model?: string;
  is_selected?: boolean;
  isSelectable?: boolean;
}

export interface ProposalEvent {
  id: string;
  proposal_id: string;
  business_id: string;
  event_type: string;
  message: string | null;
  metadata: Record<string, any> | null;
  event_timestamp: string;
  created_by: string | null;
}

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'draft':
      return {
        color: 'bg-gray-500',
        text: 'Draft',
        description: 'Not yet sent to customer'
      };
    case 'sent':
      return {
        color: 'bg-blue-500',
        text: 'Sent',
        description: 'Awaiting customer review'
      };
    case 'viewed':
      return {
        color: 'bg-purple-500',
        text: 'Viewed',
        description: 'Customer has viewed the proposal'
      };
    case 'approved':
      return {
        color: 'bg-green-500',
        text: 'Approved',
        description: 'Customer has approved an offer'
      };
    case 'declined':
      return {
        color: 'bg-red-500',
        text: 'Declined',
        description: 'Customer has declined all offers'
      };
    default:
      return {
        color: 'bg-gray-500',
        text: 'Unknown',
        description: ''
      };
  }
};

export default function ProposalDetailsModal({ 
  isOpen, 
  onClose,
  proposal,
  onUpdate,
  readOnly = false,
  onDelete
}: ProposalDetailsModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [localProposal, setLocalProposal] = useState<Proposal>(proposal);
  const [isSending, setIsSending] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [proposalEvents, setProposalEvents] = useState<ProposalEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [justSent, setJustSent] = useState(false);
  
  useEffect(() => {
    setLocalProposal(proposal);
    
    // Load proposal events when the modal is opened
    if (isOpen && proposal?.id) {
      console.log('Modal opened, loading events for proposal:', proposal.id);
      loadProposalEvents(proposal.id);
    }
  }, [proposal, isOpen]);
  
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close send options dropdown when clicking outside
  useEffect(() => {
    const closeDropdown = () => {
      setShowSendOptions(false);
    };

    if (showSendOptions) {
      document.addEventListener('click', closeDropdown);
    }

    return () => {
      document.removeEventListener('click', closeDropdown);
    };
  }, [showSendOptions]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleApproveOffer = async (offer: ProposalOffer) => {
    try {
      setLoading(true);
      setError(null);
      // Store the proposal ID before approval
      const proposalId = proposal?.id;
      await approveProposalOffer(offer.id);
      
      // Refresh the proposal data
      if (proposalId) {
        const updatedProposal = await getProposal(proposalId);
        setLocalProposal(updatedProposal);
        await onUpdate?.(updatedProposal);
      }
    } catch (err) {
      console.error('Error approving offer:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve offer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineOffer = async (offer: ProposalOffer) => {
    try {
      setLoading(true);
      setError(null);
      // Store the proposal ID before declining
      const proposalId = proposal?.id;
      await declineProposalOffer(offer.id);
      
      // Refresh the proposal data
      if (proposalId) {
        const updatedProposal = await getProposal(proposalId);
        setLocalProposal(updatedProposal);
        await onUpdate?.(updatedProposal);
      }
    } catch (err) {
      console.error('Error declining offer:', err);
      setError(err instanceof Error ? err.message : 'Failed to decline offer');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!localProposal?.id) return;
      
      await deleteProposal(localProposal.id);
      onDelete?.();
      onClose();
    } catch (err) {
      console.error('Error deleting proposal:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleSendText = async () => {
    setIsSending(true);
    setShowSendOptions(false); // Close dropdown immediately
    
    try {
      if (!localProposal?.id) {
        console.error("Proposal ID is missing");
        return;
      }

      // Get customer phone and business ID
      const customerPhone = localProposal.customer?.phone || '';
      const businessId = localProposal.business_id;
      
      if (!customerPhone) {
        console.error("Customer phone number is missing");
        return;
      }

      await sendProposalLink(localProposal.id, businessId, customerPhone);
      
      // Refresh the proposal data to get updated status
      if (localProposal.id) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate?.(updatedProposal);
      }
      
      await loadProposalEvents(localProposal.id);
      
      // Show success state
      setJustSent(true);
      setTimeout(() => setJustSent(false), 3000);
      
    } catch (error) {
      console.error("Error sending proposal via text:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendEmail = async () => {
    console.log('🔄 Starting email send process...');
    setIsSending(true);
    setShowSendOptions(false); // Close dropdown immediately
    
    try {
      if (!localProposal?.id) {
        console.log('❌ No proposal ID found');
        return;
      }
      
      // Get customer email and business ID
      const customerEmail = localProposal.customer?.email || '';
      const businessId = localProposal.business_id;
      
      console.log('📧 Email send details:', {
        proposalId: localProposal.id,
        customerEmail,
        businessId
      });
      
      if (!customerEmail) {
        console.log('❌ No customer email found');
        return;
      }
      
      console.log('📤 Calling sendProposalLinkViaEmail...');
      await sendProposalLinkViaEmail(localProposal.id, businessId, customerEmail);
      console.log('✅ Email sent successfully, now refreshing proposal data...');
      
      // Refresh the proposal data to get updated status
      if (localProposal.id) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate?.(updatedProposal);
        console.log('✅ Proposal data refreshed');
      }
      
      await loadProposalEvents(localProposal.id);
      console.log('✅ Events loaded, showing success state...');
      
      // Show success state
      setJustSent(true);
      setTimeout(() => setJustSent(false), 3000);
      console.log('✅ Success state shown!');
      
    } catch (error) {
      console.error("❌ Error sending proposal via email:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendBoth = async () => {
    setIsSending(true);
    setShowSendOptions(false); // Close dropdown immediately
    
    try {
      if (!localProposal?.id) {
        console.error("Proposal ID is missing");
        return;
      }
      
      // Get customer contact info and business ID
      const customerEmail = localProposal.customer?.email || '';
      const customerPhone = localProposal.customer?.phone || '';
      const businessId = localProposal.business_id;
      
      if (!customerEmail && !customerPhone) {
        console.error("Customer email and phone are both missing");
        return;
      }
      
      if (!customerEmail) {
        console.error("Customer email is missing");
        return;
      }
      
      if (!customerPhone) {
        console.error("Customer phone number is missing");
        return;
      }
      
      const result = await sendProposalLinkViaBoth(localProposal.id, businessId, customerEmail, customerPhone);
      
      // Refresh the proposal data to get updated status
      if (localProposal.id) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate?.(updatedProposal);
      }
      
      await loadProposalEvents(localProposal.id);
      
      // Show success state regardless of partial success
      console.log('Send both result:', result);
      setJustSent(true);
      setTimeout(() => setJustSent(false), 3000);
      
    } catch (error) {
      console.error("Error sending proposal via both methods:", error);
    } finally {
      setIsSending(false);
    }
  };

  const loadProposalEvents = async (proposalId: string) => {
    try {
      console.log('Loading proposal events for ID:', proposalId);
      setLoadingEvents(true);
      
      // First try the regular method
      const events = await getProposalEvents(proposalId);
      console.log('Retrieved proposal events via getProposalEvents:', events);
      
      if (!events || events.length === 0) {
        // If no events found, try direct query as a fallback
        console.log('No events found, trying direct query...');
        const directEvents = await directQueryProposalEvents(proposalId);
        
        if (directEvents && directEvents.length > 0) {
          console.log('Retrieved events via direct query:', directEvents);
          setProposalEvents(directEvents);
        } else {
          console.log('No events found via direct query either');
          setProposalEvents([]);
        }
      } else {
        setProposalEvents(events);
      }
    } catch (err) {
      console.error('Error loading proposal events:', err);
      // Don't set error state to avoid affecting the UI
    } finally {
      setLoadingEvents(false);
    }
  };

  const createTestEvent = async () => {
    try {
      if (!localProposal?.id) {
        console.error("No proposal ID available");
        return;
      }
      
      await createTestProposalEventSimple(localProposal.id);
      await loadProposalEvents(localProposal.id);
      
      console.log("Test event created successfully");
    } catch (error) {
      console.error("Error creating test event:", error);
    }
  };

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Check if it's today
    if (date.toDateString() === now.toDateString()) {
      return `Today at ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })}`;
    }
    
    // Check if it's yesterday
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })}`;
    }
    
    // Otherwise, return full date
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to get event type display info
  const getEventTypeInfo = (eventType: string) => {
    // Normalize the event type to lowercase for more robust matching
    const normalizedType = eventType.toLowerCase();
    
    switch (normalizedType) {
      case 'created':
        return { 
          icon: <FileText className="h-4 w-4 text-gray-600" />, 
          label: 'Proposal Created', 
          color: 'text-gray-800' 
        };
      case 'sent_sms':
        return { 
          icon: <MessageSquare className="h-4 w-4 text-blue-600" />, 
          label: 'Sent via SMS', 
          color: 'text-blue-800' 
        };
      case 'sent_email':
        return { 
          icon: <Mail className="h-4 w-4 text-blue-600" />, 
          label: 'Sent via Email', 
          color: 'text-blue-800' 
        };
      case 'viewed':
        return { 
          icon: <Eye className="h-4 w-4 text-purple-600" />, 
          label: 'Viewed by Customer', 
          color: 'text-purple-800' 
        };
      case 'offer_approved':
        return { 
          icon: <CheckCircle className="h-4 w-4 text-green-600" />, 
          label: 'Offer Approved', 
          color: 'text-green-800' 
        };
      case 'offer_declined':
        return { 
          icon: <AlertCircle className="h-4 w-4 text-red-600" />, 
          label: 'Offer Declined', 
          color: 'text-red-800' 
        };
      case 'status_changed':
      case 'status_updated':
        return { 
          icon: <RefreshCw className="h-4 w-4 text-orange-600" />, 
          label: 'Status Updated', 
          color: 'text-orange-800' 
        };
      case 'payment_received':
        return { 
          icon: <CreditCard className="h-4 w-4 text-green-600" />, 
          label: 'Payment Received', 
          color: 'text-green-800' 
        };
      case 'booked':
        return { 
          icon: <Calendar className="h-4 w-4 text-green-600" />, 
          label: 'Converted to Booking', 
          color: 'text-green-800' 
        };
      case 'test_event':
        return { 
          icon: <Activity className="h-4 w-4 text-gray-600" />, 
          label: 'Test Event', 
          color: 'text-gray-800' 
        };
      default:
        console.log('Unknown event type:', eventType);
        return { 
          icon: <Activity className="h-4 w-4 text-gray-600" />, 
          label: eventType, 
          color: 'text-gray-800' 
        };
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!localProposal) return null;

  return (
    <>
      <div 
        className={`fixed inset-0 z-[10000] transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onTransitionEnd={handleTransitionEnd}
      >
        <div 
          className={`fixed inset-0 bg-white transform transition-transform duration-300 ease-out ${
            isOpen ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="h-screen flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-[#2f2f2f] flex items-center justify-between bg-[#1a1a1a]">
              <div className="flex items-center space-x-4">
                <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
                  <X className="h-5 w-5" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-white">{localProposal.title}</h2>
                  <div className="flex items-center mt-0.5">
                    <div className={`h-1.5 w-1.5 rounded-full ${getStatusConfig(localProposal.status).color} mr-2`} />
                    <span className="text-sm text-gray-400">{getStatusConfig(localProposal.status).description}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {!readOnly && localProposal.status !== 'booked' && (
                  <button
                    onClick={handleDelete}
                    disabled={loading}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Delete
                  </button>
                )}
                {!readOnly && localProposal.status === 'approved' && (
                  <button
                    onClick={() => {
                      setIsConvertModalOpen(true);
                    }}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                  >
                    Convert to Booking
                  </button>
                )}
                {localProposal.status === 'booked' && (
                  <span className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-100 rounded-md">
                    Converted to Booking
                  </span>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-[#2f2f2f] text-gray-400 border-b border-[#3f3f3f]">
              <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between py-2 px-6">
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="font-medium text-green-400">Created</span>
                    <ChevronRight className="h-4 w-4" />
                    <span className={`font-medium ${localProposal.status === 'draft' ? 'text-gray-400' : 'text-green-400'}`}>Sent</span>
                    <ChevronRight className="h-4 w-4" />
                    <span className={`font-medium ${localProposal.status === 'draft' || localProposal.status === 'sent' ? 'text-gray-400' : 'text-green-400'}`}>Viewed</span>
                    <ChevronRight className="h-4 w-4" />
                    <span className={`font-medium ${localProposal.status !== 'approved' ? 'text-gray-400' : 'text-green-400'}`}>Approved</span>
                    <ChevronRight className="h-4 w-4" />
                    <span className={`font-medium ${localProposal.status !== 'booked' ? 'text-gray-400' : 'text-green-400'}`}>Booked</span>
                  </div>
                  <span className="text-xs text-gray-400 hidden md:block">Valid until: {localProposal.valid_until}</span>
                </div>
              </div>
            </div>

            {/* Mobile-only Valid Until */}
            <div className="md:hidden bg-gray-800 text-gray-300 text-xs text-center py-1 px-4">
              Valid until: {localProposal.valid_until}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-[#f6f6f7]">
              <div className="max-w-5xl mx-auto py-6 px-6">
                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-6">
                  {/* Customer Info */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                      <h3 className="text-sm font-medium text-gray-900">Customer</h3>
                      {!readOnly && (localProposal.status === 'draft' || localProposal.status === 'sent' || localProposal.status === 'viewed') && (
                        <div className="relative">
                        <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowSendOptions(!showSendOptions);
                            }}
                          disabled={isSending || justSent}
                          className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-colors duration-300 ${
                            justSent 
                              ? 'bg-green-600 hover:bg-green-600 focus:ring-green-500' 
                              : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                          }`}
                        >
                          {isSending ? 'Sending...' : justSent ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Sent!
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-1" />
                                {localProposal.status === 'draft' ? 'Send Proposal' : 'Send Again'}
                                <ChevronDown className="h-3 w-3 ml-1" />
                            </>
                          )}
                        </button>
                          
                          {showSendOptions && (
                            <div 
                              className="absolute right-0 mt-1 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="py-1" role="menu" aria-orientation="vertical">
                                <button
                                  onClick={handleSendText}
                                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center"
                                  role="menuitem"
                                >
                                  <MessageSquare className="h-4 w-4 mr-2" />
                                  Send Text
                                </button>
                                <button
                                  onClick={handleSendEmail}
                                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center"
                                  role="menuitem"
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  Send Email
                                </button>
                                <button
                                  onClick={handleSendBoth}
                                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center border-t border-gray-100"
                                  role="menuitem"
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Send Both
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-white font-medium">
                            {localProposal.customer?.first_name[0]}{localProposal.customer?.last_name[0]}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {localProposal.customer?.first_name} {localProposal.customer?.last_name}
                            </div>
                            <div className="text-sm text-gray-500">{localProposal.customer?.email}</div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50">
                            <Phone className="h-4 w-4" />
                          </button>
                          <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50">
                            <MessageSquare className="h-4 w-4" />
                          </button>
                          <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50">
                            <FileText className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Proposal History */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center cursor-pointer"
                         onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}>
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-medium text-gray-900">Proposal History</h3>
                        {proposalEvents.length > 0 && (
                          <span className="text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-0.5">
                            {proposalEvents.length}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            localProposal?.id && loadProposalEvents(localProposal.id);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Refresh
                        </button>
                        {isHistoryExpanded ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </div>
                    </div>
                    {isHistoryExpanded && (
                      <div className="p-3">
                        {loadingEvents ? (
                          <div className="text-center py-4">
                            <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-blue-600 border-r-transparent"></div>
                            <p className="mt-2 text-sm text-gray-500">Loading history...</p>
                          </div>
                        ) : proposalEvents.length === 0 ? (
                          <div>
                            <p className="text-sm text-gray-500 italic">No history records available yet.</p>
                            <div className="mt-4 p-3 bg-gray-50 rounded-md">
                              <p className="text-xs text-gray-700 font-medium">Diagnostic Info:</p>
                              <p className="text-xs text-gray-600">Proposal ID: {localProposal.id}</p>
                              <p className="text-xs text-gray-600">Proposal Status: {localProposal.status}</p>
                              <p className="text-xs text-gray-600">Business ID: {localProposal.business_id}</p>
                              <div className="mt-2 flex space-x-2">
                                <button 
                                  onClick={() => localProposal?.id && loadProposalEvents(localProposal.id)}
                                  className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                  Retry Load Events
                                </button>
                                <button 
                                  onClick={createTestEvent}
                                  className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                                >
                                  Create Test Event
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="max-h-[150px] overflow-y-auto pr-1 custom-scrollbar">
                            <div className="space-y-2">
                              {proposalEvents.map((event, index) => {
                                const eventInfo = getEventTypeInfo(event.event_type);
                                
                                // Determine background color based on event type
                                let bgColorClass = "bg-white";
                                if (event.event_type === 'sent_sms' || event.event_type === 'sent_email') {
                                  bgColorClass = "bg-blue-50";
                                } else if (event.event_type === 'offer_approved') {
                                  bgColorClass = "bg-green-50";
                                } else if (event.event_type === 'offer_declined') {
                                  bgColorClass = "bg-red-50";
                                } else if (event.event_type === 'viewed') {
                                  bgColorClass = "bg-purple-50";
                                } else if (event.event_type === 'test_event') {
                                  bgColorClass = "bg-gray-50";
                                }
                                
                                // Add delay based on index for staggered animation
                                const animationDelay = `${index * 50}ms`;
                                
                                return (
                                  <div 
                                    key={event.id} 
                                    className={`relative rounded-md border border-gray-200 p-2 ${bgColorClass} hover:shadow-sm transition-all duration-200 ease-in-out animate-fadeIn`}
                                    style={{ animationDelay }}
                                  >
                                    <div className="flex">
                                      <div className="mr-2 mt-0.5 flex-shrink-0">
                                        {eventInfo.icon}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                          <span className={`text-xs font-medium ${eventInfo.color} truncate mr-1`}>{eventInfo.label}</span>
                                          <span className="text-xs text-gray-500 whitespace-nowrap bg-white px-1.5 py-0.5 rounded-full border border-gray-100 flex-shrink-0">
                                            {new Date(event.event_timestamp).toLocaleTimeString('en-US', {
                                              hour: '2-digit', 
                                              minute: '2-digit',
                                              hour12: true
                                            })}
                                          </span>
                                        </div>
                                        
                                        {/* Compact event content */}
                                        <div className="text-xs">
                                          {event.message && (
                                            <p className="text-gray-600 truncate">{event.message}</p>
                                          )}
                                          
                                          {/* Compact metadata display */}
                                          {event.metadata && Object.keys(event.metadata).length > 0 && (
                                            <div className="mt-1 text-xs text-gray-600">
                                              {event.event_type === 'sent_sms' && event.metadata.to_phone && (
                                                <div className="flex items-center">
                                                  <Phone className="h-3 w-3 mr-1 text-blue-500" />
                                                  <span className="truncate">{event.metadata.to_phone}</span>
                                                </div>
                                              )}
                                              {event.event_type === 'sent_email' && event.metadata.to_email && (
                                                <div className="flex items-center">
                                                  <Mail className="h-3 w-3 mr-1 text-blue-500" />
                                                  <span className="truncate">{event.metadata.to_email}</span>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Offers */}
                  {localProposal.offers?.map((offer) => (
                    <div key={offer.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-sm font-medium text-gray-900">{offer.title}</h3>
                          {offer.status === 'approved' && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                              Selected by Customer
                            </span>
                          )}
                          {offer.status === 'declined' && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              Not Selected
                            </span>
                          )}
                        </div>
                        {offer.status === 'pending' && !readOnly && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleApproveOffer(offer)}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleDeclineOffer(offer)}
                              className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Decline
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="divide-y divide-gray-200">
                        {offer.services?.filter(service => 
                          // Only show selected services for approved offers, show all for other statuses
                          offer.status !== 'approved' || (service as any).is_selected === true
                        ).map((service) => (
                          <div key={service.id} className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-start space-x-3">
                                {service.name.toLowerCase().includes('protection') ? (
                                  <Shield className="h-4 w-4 text-blue-500 mt-0.5" />
                                ) : service.name.toLowerCase().includes('ceramic') ? (
                                  <Sparkles className="h-4 w-4 text-purple-500 mt-0.5" />
                                ) : (
                                  <Shield className="h-4 w-4 text-green-500 mt-0.5" />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center">
                                    <span className="text-sm font-medium text-gray-900">{service.name}</span>
                                    {(service as any).isSelectable && (
                                      <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                                        Optional {(service as any).is_selected ? '(Selected)' : ''}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    <RichTextDescription 
                                      description={service.description} 
                                      className="prose-sm text-gray-500 prose-headings:text-gray-600 prose-strong:text-gray-600 prose-em:text-gray-400" 
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="text-sm font-medium text-gray-900 ml-4">
                                ${service.price.toLocaleString()}
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <div className="flex items-center">
                                <Clock className="h-3.5 w-3.5 mr-1" />
                                {service.duration_minutes >= 1440 
                                  ? `${Math.round(service.duration_minutes / 1440)} days`
                                  : `${Math.round(service.duration_minutes / 60)} hours`
                                }
                              </div>
                              <div className="flex items-center">
                                <Car className="h-3.5 w-3.5 mr-1" />
                                {service.vehicle_make && service.vehicle_model 
                                  ? `${service.vehicle_make} ${service.vehicle_model}`
                                  : 'Vehicle details not available'
                                }
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="p-4 bg-gray-50">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Subtotal</span>
                            <span>${(offer.services || []).reduce((sum, service) => sum + service.price, 0).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>GST (5%)</span>
                            <span>+ ${((offer.services || []).reduce((sum, service) => sum + service.price, 0) * 0.05).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm font-medium text-gray-900 pt-2 border-t border-gray-200">
                            <span>Total</span>
                            <span>${(offer.services || []).reduce((sum, service) => sum + service.price, 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Decline All Button */}
                {localProposal.status === 'pending' && (
                  <div className="mt-6">
                    <button
                      onClick={() => {
                        localProposal.offers?.forEach(offer => handleDeclineOffer(offer));
                      }}
                      disabled={loading || readOnly}
                      className="w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50"
                    >
                      Decline All
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConvertToBookingModal
        isOpen={isConvertModalOpen}
        onClose={() => {
          setIsConvertModalOpen(false); 
        }}
        proposal={localProposal}
        onSuccess={() => {
          setIsConvertModalOpen(false);
          onClose();
        }}
      />
    </>
  );
}